import React, { useState, useEffect } from 'react';
import { Card, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Button, Box, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions, TextField, IconButton, Snackbar, Alert, Paper, Grid } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate, useParams } from 'react-router-dom';
import { FaEye, FaPlus } from 'react-icons/fa';
import ApiService from '../../../../services/api.js';
import { jsPDF } from 'jspdf';
import logo from '../../../../assets/VisibeenLogo.png';

import logger from '../../../../utils/logger';
import {
    ArrowBack as ArrowBackIcon,
    Person as PersonIcon,
    Email as EmailIcon,
    Phone as PhoneIcon,
    Work as WorkIcon,
    Badge as BadgeIcon,
    CalendarToday as CalendarTodayIcon,
    Home as HomeIcon,
    AssignmentInd as AssignmentIndIcon,
    Comment as CommentIcon,
    VpnKey as VpnKeyIcon,
    Edit as EditIcon,
    ContentCopy as ContentCopyIcon,
    Visibility as VisibilityIcon,
    VisibilityOff as VisibilityOffIcon
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';

const Container = styled(Box)(({ theme }) => ({
    padding: '32px',
    backgroundColor: '#f5f7fa',
    minHeight: '100vh',
}));

const TitleSection = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '32px',
    flexWrap: 'wrap',
    gap: '16px',
}));

const TitleIcon = styled(Box)(({ theme }) => ({
    width: '48px',
    height: '48px',
    borderRadius: '12px',
    background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
    boxShadow: '0 4px 14px rgba(59, 130, 246, 0.4)',
}));

const ViewPaper = styled(Box)(({ theme }) => ({
    maxWidth: '960px',
    margin: '0 auto',
    padding: '32px',
    borderRadius: '20px',
    boxShadow: '0 4px 25px rgba(0,0,0,0.05)',
    border: '1px solid #eef0f3',
    backgroundColor: '#fff',
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
    fontSize: '18px',
    fontWeight: 700,
    color: '#3b82f6',
    marginBottom: '24px',
    marginTop: '32px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    '&:first-of-type': {
        marginTop: 0,
    },
    '&::after': {
        content: '""',
        flex: 1,
        height: '1px',
        background: 'linear-gradient(to right, #dbeafe, transparent)',
    }
}));

const BackButton = styled(Button)(({ theme }) => ({
    textTransform: 'none',
    padding: '8px 16px',
    borderRadius: '10px',
    fontWeight: 600,
    color: '#64748b',
    border: '1px solid #eef0f3',
    '&:hover': {
        backgroundColor: '#f8fafc',
        borderColor: '#3b82f6',
        color: '#3b82f6',
    },
}));

const DetailRow = ({ icon: Icon, label, value, color }) => (
    <Box sx={{ mb: 2 }}>
        <Typography
            variant="caption"
            sx={{
                fontSize: '11px',
                fontWeight: 600,
                color: '#64748b',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mb: 0.5,
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
            }}
        >
            {Icon && <Icon sx={{ fontSize: 13, color: color || '#3b82f6' }} />} {label}
        </Typography>
        <Typography
            variant="body1"
            sx={{
                fontSize: '15px',
                fontWeight: 600,
                color: '#1f2937',
                pl: Icon ? 2.5 : 0
            }}
        >
            {value || '—'}
        </Typography>
    </Box>
);

const EmployeeView = (props) => {
    const [employee, setEmployee] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [showPassword, setShowPassword] = useState(false);
    const navigate = useNavigate();
    const { id: routeId } = useParams();
    const propEmployeeId = props && props.employeeId;
    const resolvedEmployeeId = propEmployeeId || routeId || (typeof window !== 'undefined' ? window.location.pathname.split('/').pop() : undefined) || '3';

    // Reset Password State
    const [resetDialogOpen, setResetDialogOpen] = useState(false);
    const [newPassword, setNewPassword] = useState('');
    const [confirmReset, setConfirmReset] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    // Roles mapping
    const ROLES = {
        1: 'Super Admin',
        2: 'Admin',
        'bdm_outside': 'BDM Sales (outside)',
        'bdm_inside': 'BDM Sales (inside)',
        'cro': 'CRO',
        4: 'Employee'
    };

    const handleCloseSnackbar = () => setSnackbar({ ...snackbar, open: false });

    const handleOpenResetDialog = () => {
        setNewPassword('');
        setConfirmReset(false);
        setResetDialogOpen(true);
    };

    const generateNewPassword = () => {
        const firstName = employee.first_name || employee.name?.split(' ')[0] || 'user';
        const random6Digits = Math.floor(100000 + Math.random() * 900000);
        const password = `${firstName}@${random6Digits}#`.replace(/\s/g, '');
        setNewPassword(password);
        setConfirmReset(true);
    };

    const confirmPasswordReset = async () => {
        try {
            await ApiService.post('/admin/employee/reset-password', {
                employee_id: employee.id,
                new_password: newPassword
            });
            setSnackbar({ open: true, message: 'Password reset successfully!', severity: 'success' });
            // Keep dialog open to show the new password so they can copy/download
        } catch (err) {
            setSnackbar({ open: true, message: 'Failed to reset password.', severity: 'error' });
            console.error(err);
        }
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        setSnackbar({ open: true, message: 'Copied to clipboard!', severity: 'success' });
    };

    const downloadCredentialsPDF = () => {
        const doc = new jsPDF();
        const pageWidth = doc.internal.pageSize.getWidth();

        try {
            doc.addImage(logo, 'PNG', pageWidth / 2 - 25, 15, 50, 15);
        } catch (err) { console.warn('Logo err', err); }

        doc.setFont("helvetica", "bold");
        doc.setFontSize(22);
        doc.setTextColor(59, 130, 246);
        doc.text("Credentials Reset", pageWidth / 2, 45, { align: "center" });

        doc.setDrawColor(59, 130, 246);
        doc.setLineWidth(0.5);
        doc.line(20, 50, pageWidth - 20, 50);

        doc.setFont("helvetica", "normal");
        doc.setFontSize(12);
        doc.setTextColor(60, 60, 60);
        doc.text(`Employee Name: ${employee.first_name} ${employee.last_name}`, 20, 70);
        doc.text(`Official Email: ${employee.email}`, 20, 80);
        doc.text(`Date: ${new Date().toLocaleDateString()}`, 20, 90);

        doc.setFillColor(252, 252, 253);
        doc.setDrawColor(220, 220, 220);
        doc.rect(20, 105, pageWidth - 40, 60, 'FD');

        doc.setFont("helvetica", "bold");
        doc.setFontSize(14);
        doc.setTextColor(31, 41, 55);
        doc.text("New Login Details", 30, 120);

        doc.setFontSize(11);
        doc.setTextColor(107, 114, 128);
        doc.text("USERNAME", 30, 135);

        doc.setFont("courier", "bold");
        doc.setFontSize(14);
        doc.setTextColor(0, 0, 0);
        doc.text(employee.email || '', 30, 143);

        doc.setFont("helvetica", "normal");
        doc.setFontSize(11);
        doc.setTextColor(107, 114, 128);
        doc.text("NEW PASSWORD", 30, 158);

        doc.setFont("courier", "bold");
        doc.setFontSize(14);
        doc.setTextColor(0, 0, 0);
        doc.text(newPassword, 30, 166);

        doc.save(`${employee.first_name}_New_Credentials.pdf`);
    };

    // Helper to safely render values that may be objects/arrays
    const displayValue = (val) => {
        if (val === null || val === undefined) return 'N/A';
        if (typeof val === 'object') {
            if (Array.isArray(val)) {
                const parts = val.map((item) => displayValue(item)).filter(Boolean);
                return parts.length ? parts.join(', ') : 'N/A';
            }
            if ('name' in val && typeof val.name !== 'object') return String(val.name ?? 'N/A');
            try {
                return JSON.stringify(val);
            } catch (e) {
                return 'N/A';
            }
        }
        const s = String(val).trim();
        return s.length ? s : 'N/A';
    };

    useEffect(() => {
        fetchEmployeeDetails();
    }, []);

    const fetchEmployeeDetails = async () => {
        setLoading(true);
        setError(null);
        try {
            const employeeId = resolvedEmployeeId;

            const response = await ApiService.get(`/admin/employee/get-employee/${employeeId}`);

            logger.log('API Response:', response);

            if (response && response.data && response.data.data) {
                setEmployee(response.data.data);
            } else if (response && response.data) {
                setEmployee(response.data);
            } else if (response) {
                setEmployee(response);
            } else {
                throw new Error('Invalid response format');
            }
        } catch (err) {
            logger.error('Error fetching employee details:', err);
            setError('Failed to fetch employee details. Using mock data for demonstration.');

            const employeeId = resolvedEmployeeId;
            setEmployee({
                id: employeeId,
                name: 'Robert Johnson',
                email: 'robert.johnson@example.com',
                phone_number: '+1 345-678-9012',
                position: 'UX Designer',
                department: 'Design',
                status: 'Active',
                join_date: '2022-03-20',
                employee_code: 'EMP003',
                employee_type: 'Contract'
            });
        } finally {
            setLoading(false);
        }
    };

    const handleBack = () => {
        // Navigate back to employee listing
        navigate('/admin-dashboard/employee');
    };

    return (
        <Container>
            <TitleSection>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <TitleIcon>
                        <PersonIcon sx={{ fontSize: 26 }} />
                    </TitleIcon>
                    <Box>
                        <Typography variant="h4" fontWeight={700} color="#1f2937">Employee Profile</Typography>
                        <Typography variant="body2" color="text.secondary">Detailed view of team member information</Typography>
                    </Box>
                </Box>
                <BackButton onClick={handleBack} startIcon={<ArrowBackIcon />}>
                    Back to Listing
                </BackButton>
            </TitleSection>

            <ViewPaper>
                {loading ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '400px', gap: 2 }}>
                        <CircularProgress size={50} sx={{ color: '#3b82f6' }} />
                        <Typography variant="body1" color="text.secondary" fontWeight={500}>
                            Pulling employee data...
                        </Typography>
                    </Box>
                ) : error ? (
                    <Box sx={{ bgcolor: '#fef2f2', color: '#dc2626', p: 3, borderRadius: '16px', textAlign: 'center', border: '1px solid #fee2e2' }}>
                        <Typography fontWeight={600}>{error}</Typography>
                    </Box>
                ) : employee ? (
                    <Box>
                        {/* Header Badge */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 5, pb: 4, borderBottom: '1px solid #f1f5f9' }}>
                            <Box
                                sx={{
                                    width: 80, height: 80, borderRadius: '20px',
                                    background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    color: '#fff', fontSize: '32px', fontWeight: 700,
                                    boxShadow: '0 8px 16px rgba(59, 130, 246, 0.2)'
                                }}
                            >
                                {(([employee.first_name || employee.name || 'N'])[0]).toUpperCase()}
                            </Box>
                            <Box>
                                <Typography variant="h5" fontWeight={700} color="#1f2937">
                                    {`${employee.first_name || ''} ${employee.last_name || ''}`.trim() || displayValue(employee.name)}
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                    <BadgeIcon sx={{ fontSize: 16, color: '#3b82f6' }} />
                                    <Typography variant="body2" color="#64748b" fontWeight={600}>
                                        {displayValue(employee.employee_code)} • {displayValue(employee.employee_type)}
                                    </Typography>
                                </Box>
                                <Box
                                    sx={{
                                        mt: 1.5, display: 'inline-block', px: 1.5, py: 0.5, borderRadius: '6px',
                                        bgcolor: displayValue(employee.status) === 'Active' ? alpha('#10b981', 0.1) : alpha('#ef4444', 0.1),
                                        color: displayValue(employee.status) === 'Active' ? '#059669' : '#dc2626',
                                        fontSize: '12px', fontWeight: 700, textTransform: 'uppercase'
                                    }}
                                >
                                    {displayValue(employee.status)}
                                </Box>
                            </Box>
                        </Box>

                        <SectionTitle>
                            <AssignmentIndIcon fontSize="small" /> Basic Information
                        </SectionTitle>
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 4 }}>
                            <DetailRow icon={WorkIcon} label="Department" value={displayValue(employee.department)} />
                            <DetailRow icon={AssignmentIndIcon} label="Designation / Position" value={displayValue(employee.designation || employee.position || employee.role)} />
                            <DetailRow icon={EmailIcon} label="Official Email" value={displayValue(employee.email)} color="#3b82f6" />
                            <DetailRow icon={PhoneIcon} label="Primary Contact" value={displayValue(employee.phone || employee.phone_number)} color="#10b981" />
                            <DetailRow icon={CalendarTodayIcon} label="Date of Joining" value={displayValue(employee.date_of_joining || employee.joining_date || employee.doj)} />
                        </Box>

                        <SectionTitle>
                            <VpnKeyIcon fontSize="small" /> Role & Credentials
                        </SectionTitle>
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 4 }}>
                            <DetailRow label="Assigned Role" value={ROLES[employee.role_id] || `Role ${employee.role_id}` || 'Employee'} color="#8b5cf6" />

                            {/* Username Display */}
                            <Box>
                                <Typography variant="caption" sx={{ fontSize: '11px', fontWeight: 600, color: '#64748b', display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                    <EmailIcon sx={{ fontSize: 13, color: '#3b82f6' }} /> USERNAME / LOGIN EMAIL
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography variant="body1" sx={{ fontSize: '15px', fontWeight: 600, color: '#1f2937', pl: 2.5 }}>
                                        {displayValue(employee.official_email || employee.email)}
                                    </Typography>
                                    {(employee.official_email || employee.email) && (
                                        <IconButton size="small" onClick={() => copyToClipboard(employee.official_email || employee.email)} sx={{ color: '#6366f1' }}>
                                            <ContentCopyIcon fontSize="small" />
                                        </IconButton>
                                    )}
                                </Box>
                            </Box>

                            {/* Password Display */}
                            <Box>
                                <Typography variant="caption" sx={{ fontSize: '11px', fontWeight: 600, color: '#64748b', display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                    <VpnKeyIcon sx={{ fontSize: 13, color: '#10b981' }} /> PASSWORD
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: 2.5 }}>
                                    {employee.plain_password ? (
                                        <>
                                            <Typography variant="body1" sx={{ fontSize: '15px', fontWeight: 600, color: '#1f2937', minWidth: '80px' }}>
                                                {showPassword ? employee.plain_password : '••••••••'}
                                            </Typography>
                                            <IconButton size="small" onClick={() => setShowPassword(!showPassword)}>
                                                {showPassword ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                                            </IconButton>
                                            <IconButton size="small" onClick={() => copyToClipboard(employee.plain_password)} sx={{ color: '#6366f1' }}>
                                                <ContentCopyIcon fontSize="small" />
                                            </IconButton>
                                        </>
                                    ) : (
                                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', color: '#ca8a04' }}>
                                            Not stored
                                        </Typography>
                                    )}
                                </Box>
                            </Box>

                            <Box sx={{ gridColumn: '1 / -1', mt: 1 }}>
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    startIcon={<EditIcon />}
                                    onClick={handleOpenResetDialog}
                                    sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600 }}
                                >
                                    Reset Password
                                </Button>
                            </Box>
                        </Box>

                        <SectionTitle>
                            <HomeIcon fontSize="small" /> Family & Address
                        </SectionTitle>
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 4 }}>
                            <DetailRow label="Family Member" value={displayValue(employee.family_member_name)} />
                            <DetailRow label="Relation" value={displayValue(employee.family_relation)} />
                            <DetailRow label="Family Contact" value={displayValue(employee.family_contact)} />
                        </Box>

                        <Box sx={{ mt: 3 }}>
                            <Typography variant="h6" sx={{ fontWeight: 600, color: '#3b82f6', mb: 2 }}>Residential Address</Typography>
                            <DetailRow icon={HomeIcon} label="Address Line 1" value={displayValue(employee.current_address_line1)} />
                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr 1fr' }, gap: 4, mt: 1 }}>
                                <DetailRow label="City" value={displayValue(employee.current_city)} />
                                <DetailRow label="State" value={displayValue(employee.current_state)} />
                                <DetailRow label="Postal Code" value={displayValue(employee.current_zip_code)} />
                                <DetailRow label="Country" value="India" />
                            </Box>
                        </Box>

                        <Box sx={{ mt: 3 }}>
                            <Typography variant="h6" sx={{ fontWeight: 600, color: '#3b82f6', mb: 2 }}>Permanent Address</Typography>
                            <DetailRow icon={HomeIcon} label="Address Line 1" value={displayValue(employee.permanent_address_line1)} />
                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr 1fr' }, gap: 4, mt: 1 }}>
                                <DetailRow label="City" value={displayValue(employee.permanent_city)} />
                                <DetailRow label="State" value={displayValue(employee.permanent_state)} />
                                <DetailRow label="Postal Code" value={displayValue(employee.permanent_zip_code)} />
                                <DetailRow label="Country" value="India" />
                            </Box>
                        </Box>

                        <SectionTitle>
                            <CommentIcon fontSize="small" /> Other Details
                        </SectionTitle>
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 4 }}>
                            <DetailRow icon={AssignmentIndIcon} label="Reporting Manager" value={(() => {
                                const managers = [
                                    { id: 1, first_name: 'Amardeep', last_name: '' },
                                    { id: 2, first_name: 'Ruchi', last_name: '' },
                                    { id: 3, first_name: 'Jyoti', last_name: '' }
                                ];
                                const manager = managers.find(m => m.id === employee.report_to);
                                return manager ? `${manager.first_name} ${manager.last_name}`.trim() : displayValue(employee.report_to);
                            })()} />
                            <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#3b82f6', fontSize: '14px', mb: 1 }}>
                                    📎 Supporting Documents
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    No documents uploaded
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                ) : (
                    <Box sx={{ textAlign: 'center', py: 10 }}>
                        <Typography color="text.secondary">No employee data discovered</Typography>
                    </Box>
                )}
            </ViewPaper>

            {/* Reset Password Dialog */}
            <Dialog open={resetDialogOpen} onClose={() => setResetDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontWeight: 700, color: '#1f2937' }}>Reset Employee Password</DialogTitle>
                <DialogContent>
                    {!confirmReset ? (
                        <Box sx={{ py: 2 }}>
                            <Typography variant="body1" sx={{ mb: 3 }}>
                                Are you sure you want to reset the password for <b>{employee?.first_name} {employee?.last_name}</b>?
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                This will generate a new random password and update the system immediately. The old password will stop working.
                            </Typography>
                            <Button variant="contained" color="primary" fullWidth onClick={generateNewPassword} sx={{ py: 1.5, fontWeight: 700, borderRadius: '8px' }}>
                                Generate New Password
                            </Button>
                        </Box>
                    ) : (
                        <Box sx={{ py: 2 }}>
                            <Alert severity="success" sx={{ mb: 3 }}>
                                Password has been reset successfully!
                            </Alert>
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 2,
                                    borderRadius: '12px',
                                    background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.05) 0%, rgba(217, 119, 6, 0.05) 100%)',
                                    border: '1px solid rgba(59, 130, 246, 0.2)',
                                }}
                            >
                                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#3b82f6', mb: 2 }}>
                                    🔐 New Login Credentials
                                </Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                            NEW PASSWORD
                                        </Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                            <Typography variant="h6" sx={{ fontWeight: 700, fontFamily: 'monospace' }}>
                                                {newPassword}
                                            </Typography>
                                            <IconButton size="small" onClick={() => copyToClipboard(newPassword)} sx={{ color: '#3b82f6' }}>
                                                <ContentCopyIcon fontSize="small" />
                                            </IconButton>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </Paper>
                            <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                                <Button variant="outlined" fullWidth onClick={downloadCredentialsPDF}>Download PDF</Button>
                                <Button variant="contained" fullWidth onClick={() => { confirmPasswordReset(); setResetDialogOpen(false); }}>Done</Button>
                            </Box>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    {!confirmReset && <Button onClick={() => setResetDialogOpen(false)}>Cancel</Button>}
                </DialogActions>
            </Dialog>

            <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default EmployeeView;